import { Button, Input, Modal, RichTextEditor } from "@legal-500/legal500-design-system";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { cms } from "../../../api/cms.service";
import { CmsImageUploadField } from "../../../components/CMS/CmsImageUploadField/CmsImageUploadField";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal";
import type { CmsBlock } from "../../../types/cms";
import type { CardGridCard, CardModalGridCard } from "./types";
import styles from "../CMS.module.css";

type CardGridFormValues = Omit<CardGridCard, "id">;
type CardModalGridFormValues = Omit<CardModalGridCard, "id">;

type CardEditModalProps = {
  block: CmsBlock;
  cardIndex: number;
  mode?: "edit" | "create";
  onUploadingChange?: (uploading: boolean) => void;
  onSaved: () => void;
  onClose: () => void;
};

function isCardGridBlock(block: CmsBlock): boolean {
  return block.type === "block_card_grid";
}

function isCardModalGridBlock(block: CmsBlock): boolean {
  return block.type === "block_card_modal_grid";
}

function getCards(block: CmsBlock): Array<CardGridCard | CardModalGridCard> {
  const content = block.content as { cards?: Array<CardGridCard | CardModalGridCard> } | null;
  return Array.isArray(content?.cards) ? content.cards : [];
}

function CardGridCardForm({
  card,
  cardIndex,
  onSave,
  onDelete,
  onCancel,
  saving,
}: {
  card: CardGridCard;
  cardIndex: number;
  onSave: (values: CardGridFormValues) => void;
  onDelete: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const { register, handleSubmit } = useForm<CardGridFormValues>({
    defaultValues: {
      order: card.order ?? cardIndex,
      title: card.title,
      href: card.href,
      text: card.text,
    },
  });

  return (
    <form
      onSubmit={handleSubmit(onSave)}
      style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
    >
      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="card-edit-order">
          Order
        </label>
        <Input id="card-edit-order" type="number" {...register("order", { valueAsNumber: true })} />
      </div>

      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="card-edit-title">
          Title
        </label>
        <Input id="card-edit-title" {...register("title")} />
      </div>

      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="card-edit-href">
          Link (href)
        </label>
        <Input id="card-edit-href" {...register("href")} />
      </div>

      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="card-edit-text">
          Description
        </label>
        <Input id="card-edit-text" {...register("text")} />
      </div>

      <div className={styles.formActions}>
        <div className={styles.formActionsLeft}>
          <Button variant="outlined" color="error" onClick={onDelete} disabled={saving}>
            Delete Card
          </Button>
        </div>
        <Button variant="outlined" color="default" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={saving}>
          {saving ? "Saving..." : "Save Card"}
        </Button>
      </div>
    </form>
  );
}

function CardModalGridCardForm({
  card,
  cardIndex,
  onSave,
  onDelete,
  onCancel,
  saving,
  creating,
  onUploadingChange,
}: {
  card: CardModalGridCard;
  cardIndex: number;
  onSave: (values: CardModalGridFormValues) => void;
  onDelete: () => void;
  onCancel: () => void;
  saving: boolean;
  creating: boolean;
  onUploadingChange?: (uploading: boolean) => void;
}) {
  const { register, handleSubmit, control } = useForm<CardModalGridFormValues>({
    defaultValues: {
      order: card.order ?? cardIndex,
      heading: card.heading,
      subheading: card.subheading,
      image: card.image,
      description: card.description,
      socialLink: card.socialLink ?? "",
    },
  });

  const [uploading, setUploading] = useState(false);

  return (
    <form
      onSubmit={handleSubmit(values => {
        if (!uploading) onSave(values);
      })}
      style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
    >
      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="card-edit-order">
          Order
        </label>
        <Input id="card-edit-order" type="number" {...register("order", { valueAsNumber: true })} />
      </div>

      <div className={styles.cardEditorFieldRow}>
        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="card-edit-heading">
            Heading
          </label>
          <Input id="card-edit-heading" {...register("heading")} />
        </div>

        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="card-edit-subheading">
            Subheading
          </label>
          <Input id="card-edit-subheading" {...register("subheading")} />
        </div>
      </div>

      <div className={styles.cardEditorFieldRow}>
        <div className={styles.formField}>
          <Controller
            name="image"
            control={control}
            render={({ field }) => (
              <CmsImageUploadField
                value={field.value}
                cardLabel={
                  cardIndex < 0
                    ? "New card"
                    : `Card ${cardIndex + 1}: ${card.heading || "Untitled"}`
                }
                onChange={field.onChange}
                disabled={saving}
                onUploadingChange={pending => {
                  setUploading(pending);
                  onUploadingChange?.(pending);
                }}
              />
            )}
          />
        </div>

        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="card-edit-social">
            Social Link (optional)
          </label>
          <Input id="card-edit-social" {...register("socialLink")} />
        </div>
      </div>

      <div className={styles.formField}>
        <Controller
          name="description"
          control={control}
          render={({ field: { value, onChange } }) => (
            <RichTextEditor
              id="card-edit-description"
              value={value}
              onChange={onChange}
              preset="basic"
              label={`Description for ${card.heading || "this card"}`}
              minHeight={120}
            />
          )}
        />
      </div>

      <div className={styles.formActions}>
        <div className={styles.formActionsLeft}>
          {!creating && (
            <Button
              type="button"
              variant="outlined"
              color="error"
              onClick={onDelete}
              disabled={saving || uploading}
            >
              Delete Card
            </Button>
          )}
        </div>
        <Button variant="outlined" color="default" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={saving || uploading}>
          {saving ? "Saving..." : "Save Card"}
        </Button>
      </div>
    </form>
  );
}

export function CardEditModal({
  block,
  cardIndex,
  mode = "edit",
  onSaved,
  onClose,
  onUploadingChange,
}: CardEditModalProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const cards = getCards(block);
  const card =
    mode === "create"
      ? {
          id: cards.length ? Math.max(...cards.map(card => card.id)) + 1 : 1,
          order: cards.length ? Math.max(...cards.map(card => card.order ?? 0)) + 1 : 0,
          heading: "",
          subheading: "",
          image: "",
          description: "",
          socialLink: "",
        }
      : cards[cardIndex];

  if (!card) {
    return (
      <Modal isOpen onClose={onClose} title="Edit Card" size="medium">
        <p>Card not found at index {cardIndex}.</p>
        <div className={styles.formActions}>
          <Button variant="outlined" color="default" onClick={onClose}>
            Close
          </Button>
        </div>
      </Modal>
    );
  }

  async function deleteCard() {
    await cms.updateBlock(block.id, {
      content: { ...block.content, cards: cards.filter((_, i) => i !== cardIndex) },
    });
    onSaved();
  }

  function stripWrappingParagraph(html: string): string {
    const trimmed = html.trim();
    if (trimmed.startsWith("<p>") && trimmed.endsWith("</p>") && trimmed.indexOf("<p>", 1) === -1) {
      return trimmed.slice(3, -4);
    }
    return trimmed;
  }

  async function saveUpdatedCard(updatedValues: CardGridFormValues | CardModalGridFormValues) {
    setSaving(true);
    setError(null);

    try {
      const cleaned = { ...updatedValues };
      if ("description" in cleaned && typeof cleaned.description === "string") {
        cleaned.description = stripWrappingParagraph(cleaned.description);
      }

      const updatedCards =
        mode === "create"
          ? [...cards, { ...card, ...cleaned }]
          : cards.map((c, i) => {
              if (i !== cardIndex) return c;
              return { ...c, ...cleaned };
            });

      await cms.updateBlock(block.id, {
        content: { ...block.content, cards: updatedCards } as unknown as Record<string, unknown>,
      });

      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save card");
      setSaving(false);
    }
  }

  const cardNumber = cardIndex + 1;
  const isGrid = isCardGridBlock(block);
  const isModalGrid = isCardModalGridBlock(block);

  const title =
    mode === "create"
      ? "Add Card"
      : isGrid
        ? `Edit Card ${cardNumber}: ${(card as CardGridCard).title || "Untitled"}`
        : isModalGrid
          ? `Edit Card ${cardNumber}: ${(card as CardModalGridCard).heading || "Untitled"}`
          : `Edit Card ${cardNumber}`;

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!saving) onClose();
      }}
      title={title}
      size="medium"
    >
      {error && <div className={styles.errorBanner}>{error}</div>}

      {isGrid && (
        <CardGridCardForm
          card={card as CardGridCard}
          cardIndex={cardIndex}
          onSave={saveUpdatedCard}
          onDelete={() => setConfirmDelete(true)}
          onCancel={onClose}
          saving={saving}
        />
      )}

      {isModalGrid && (
        <CardModalGridCardForm
          onUploadingChange={onUploadingChange}
          creating={mode === "create"}
          card={card as CardModalGridCard}
          cardIndex={cardIndex}
          onSave={saveUpdatedCard}
          onDelete={() => setConfirmDelete(true)}
          onCancel={onClose}
          saving={saving}
        />
      )}
      {confirmDelete && (
        <ConfirmationModal
          title="Delete Card"
          message="Are you sure you want to delete this card?"
          warningMessage="This action cannot be undone."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          onClose={() => setConfirmDelete(false)}
          onConfirm={deleteCard}
        />
      )}
    </Modal>
  );
}
